import React, { useState, useEffect, useMemo } from 'react';
import { signInWithEmailAndPassword, signInWithPopup, OAuthProvider, Auth } from 'firebase/auth';
import { getFirebaseAuth } from '../firebase';

// Mosaic tile component
const MosaicTile: React.FC<{
  x: number;
  y: number;
  size: number;
  delay: number;
  baseColor: string;
}> = ({ x, y, size, delay, baseColor }) => {
  return (
    <div
      style={{
        position: 'absolute',
        left: x,
        top: y,
        width: size,
        height: size,
        background: baseColor,
        opacity: 0.4,
        animation: `mosaicPulse ${3 + Math.random() * 4}s ease-in-out ${delay}s infinite`,
        clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
      }}
    />
  );
};

// Generate mosaic tiles
const generateTiles = (width: number, height: number) => {
  const tiles: Array<{
    id: number;
    x: number;
    y: number;
    size: number;
    delay: number;
    color: string;
  }> = [];
  
  const colors = [
    'rgba(0, 128, 128, 0.6)',   // Teal
    'rgba(0, 107, 136, 0.5)',   // Blue-teal
    'rgba(34, 139, 84, 0.5)',   // Green
    'rgba(0, 71, 119, 0.6)',    // Navy
    'rgba(46, 139, 87, 0.4)',   // Sea green
    'rgba(0, 32, 63, 0.7)',     // Dark navy
    'rgba(0, 150, 136, 0.4)',   // Bright teal
    'rgba(128, 0, 128, 0.5)',   // Purple
    'rgba(106, 27, 154, 0.5)',  // Deep purple
    'rgba(156, 39, 176, 0.4)',  // Medium purple
    'rgba(74, 20, 140, 0.6)',   // Dark purple
    'rgba(255, 152, 0, 0.5)',   // Orange
    'rgba(230, 126, 34, 0.5)',  // Burnt orange
    'rgba(255, 183, 77, 0.4)',  // Light orange
    'rgba(183, 110, 0, 0.6)',   // Dark orange
  ];

  let id = 0;
  const baseSize = 60;
  const hexHeight = baseSize;
  const hexWidth = baseSize;
  const rowSpacing = hexHeight * 0.75;
  
  for (let row = -1; row < Math.ceil(height / rowSpacing) + 1; row++) {
    const isOddRow = row % 2 !== 0;
    const rowOffset = isOddRow ? hexWidth * 0.5 : 0;
    for (let col = -1; col < Math.ceil(width / hexWidth) + 1; col++) {
      // Add some randomness to sizes
      const sizeVariation = 0.7 + Math.random() * 0.6;
      const size = baseSize * sizeVariation;
      const offsetX = (Math.random() - 0.5) * 10;
      const offsetY = (Math.random() - 0.5) * 10;
      
      tiles.push({
        id: id++,
        x: col * hexWidth + rowOffset + offsetX,
        y: row * rowSpacing + offsetY,
        size,
        delay: Math.random() * 5,
        color: colors[Math.floor(Math.random() * colors.length)],
      });
    }
  }
  
  return tiles;
};

export const Login: React.FC = () => {
  const [authInstance, setAuthInstance] = useState<Auth | null>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [dimensions, setDimensions] = useState({ width: 1920, height: 1080 });

  useEffect(() => {
    getFirebaseAuth().then(setAuthInstance).catch(console.error);
  }, []);

  useEffect(() => {
    const updateDimensions = () => {
      setDimensions({ width: window.innerWidth, height: window.innerHeight });
    };
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  const tiles = useMemo(
    () => generateTiles(dimensions.width, dimensions.height),
    [dimensions.width, dimensions.height]
  );

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!authInstance) return;

    setError('');
    setIsLoading(true);

    try {
      const loginEmail = `${username}@mosaics.io`;
      await signInWithEmailAndPassword(authInstance, loginEmail, password);
    } catch (err: any) {
      console.error(err);
      if (err.code === 'auth/invalid-credential' || err.code === 'auth/user-not-found') {
        setError('Invalid username or password.');
      } else {
        setError('Login failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSSOLogin = async () => {
    if (!authInstance) return;
    setError('');
    setIsLoading(true);
    try {
      const provider = new OAuthProvider('microsoft.com');
      provider.setCustomParameters({
        tenant: 'culmen.com',
      });
      const result = await signInWithPopup(authInstance, provider);
      // Auto-provision user on first login — call server endpoint.
      // signInWithPopup fires onAuthStateChanged before it resolves, so
      // LoginPage's redirect is already racing us — we can't await here
      // without a more invasive refactor. Two guardrails instead:
      //   1. keepalive: true — browser keeps this request in flight even
      //      if the tab navigates or closes mid-fetch. This survives SPA
      //      route changes AND a hard tab close within a few seconds.
      //   2. Server-side self-heal in verifyToken (middleware/auth.ts) —
      //      any authenticated request from a Culmen-SSO user with no
      //      memberships lazily provisions {culmen: 'user'}, so even if
      //      this fire-and-forget somehow doesn't land, the very next
      //      authenticated request repairs the account.
      const token = await result.user.getIdToken();
      fetch('/api/user/ensure-sso', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        keepalive: true,
      })
        .then(async (r) => {
          if (!r.ok) console.error(`[SSO] ensure-sso HTTP ${r.status}: ${await r.text().catch(() => '')}`);
        })
        .catch((err) => console.error('[SSO] ensure-sso network failed:', err));
      // Auth state change in AuthGate will handle the redirect — we're done
    } catch (err: any) {
      console.error('SSO login error:', err?.code, err?.message, err);
      if (err.code === 'auth/popup-closed-by-user') {
        setError('Login cancelled.');
      } else if (err.code === 'auth/account-exists-with-different-credential') {
        setError('An account already exists with this email using a different sign-in method.');
      } else {
        setError(`SSO login failed: ${err?.message || 'Unknown error'}`);
      }
      setIsLoading(false);
    }
  };

  return (
    <>
      <style>{`
        .login-input::placeholder {
          color: #7dd3c0;
        }
        
        @keyframes mosaicPulse {
          0%, 100% {
            opacity: 0.2;
            transform: scale(1) rotate(0deg);
          }
          50% {
            opacity: 0.5;
            transform: scale(1.1) rotate(2deg);
          }
        }
        
        @keyframes mosaicFloat {
          0%, 100% {
            transform: translateY(0) rotate(0deg);
          }
          50% {
            transform: translateY(-10px) rotate(1deg);
          }
        }
        
        @keyframes shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }
      `}</style>
      
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #00203F 0%, #004777 35%, #006B88 65%, #00203F 100%)',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          padding: 16,
          overflow: 'hidden',
        }}
      >
        {/* Mosaic Background */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            overflow: 'hidden',
            opacity: 0.7,
          }}
        >
          {tiles.map((tile) => (
            <MosaicTile
              key={tile.id}
              x={tile.x}
              y={tile.y}
              size={tile.size}
              delay={tile.delay}
              baseColor={tile.color}
            />
          ))}
          
          {/* Overlay gradient for depth */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'radial-gradient(ellipse at center, transparent 0%, rgba(0,32,63,0.6) 100%)',
              pointerEvents: 'none',
            }}
          />
        </div>

        <div style={{ position: 'relative', width: '100%', maxWidth: 400, zIndex: 10 }}>
          {/* Glow */}
          <div
            style={{
              position: 'absolute',
              inset: -16,
              background: 'linear-gradient(to right, rgba(0,128,128,0.24), rgba(34,139,84,0.24), rgba(0,107,136,0.24))',
              borderRadius: 32,
              filter: 'blur(32px)',
              zIndex: 0,
            }}
          />

          {/* Card */}
          <div
            style={{
              position: 'relative',
              zIndex: 1,
              background: 'rgba(0, 32, 63, 0.85)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: 20,
              overflow: 'hidden',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            }}
          >
            {/* Gradient bar with shimmer */}
            <div 
              style={{ 
                height: 6, 
                background: 'linear-gradient(90deg, #00203F, #004777, #6A1B9A, #006B88, #008080, #E67E22, #228B54, #9C27B0, #FF9800, #006B88, #004777, #00203F)',
                backgroundSize: '200% 100%',
                animation: 'shimmer 8s linear infinite',
              }} 
            />

            {/* Header */}
            <div style={{ padding: '32px 32px 24px', textAlign: 'center' }}>
              <img
                src="/mosaics_logo_optimized.png"
                alt="Mosaics Logo"
                style={{
                  width: 120,
                  height: 120,
                  marginBottom: 5,
                  objectFit: 'contain',
                }}
              />
              <img
                src="/mosaics_word.png"
                alt="Mosaics Logo"
                style={{
                  display: 'block',
                  margin: '0 auto 16px',
                  height: 36,
                  objectFit: 'contain',
                }}
              />              
              <p style={{ margin: '4px 0 0', fontSize: 14, color: '#5eead4' }}>Geospatial Analytics Platform</p>
            </div>

            {/* Divider */}
            <div
              style={{
                margin: '0 32px',
                height: 1,
                background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.2), transparent)',
              }}
            />

            {/* Form */}
            <div style={{ padding: 32 }}>
              <form onSubmit={handleLogin}>
                {error && (
                  <div
                    style={{
                      marginBottom: 20,
                      padding: 12,
                      background: 'rgba(185, 28, 28, 0.4)',
                      border: '1px solid rgba(239, 68, 68, 0.6)',
                      borderRadius: 8,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                    }}
                  >
                    <p style={{
                      margin: 0,
                      color: '#ffe4e6',
                      fontSize: 14,
                      fontWeight: 500
                    }}>
                      {error}
                    </p>
                  </div>
                )}

                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', color: '#e2e8f0', fontSize: 14, fontWeight: 500, marginBottom: 8 }}>
                    Username
                  </label>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Enter your username"
                    autoComplete="username"
                    required
                    className="login-input"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      background: 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(0,128,128,0.3)',
                      borderRadius: 12,
                      color: 'white',
                      fontSize: 14,
                      outline: 'none',
                      boxSizing: 'border-box',
                      transition: 'border-color 0.2s, box-shadow 0.2s',
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = 'rgba(0,128,128,0.6)';
                      e.target.style.boxShadow = '0 0 0 3px rgba(0,128,128,0.15)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(0,128,128,0.3)';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>

                <div style={{ marginBottom: 24 }}>
                  <label style={{ display: 'block', color: '#e2e8f0', fontSize: 14, fontWeight: 500, marginBottom: 8 }}>
                    Password
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    required
                    className="login-input"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      background: 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(0,128,128,0.3)',
                      borderRadius: 12,
                      color: 'white',
                      fontSize: 14,
                      outline: 'none',
                      boxSizing: 'border-box',
                      transition: 'border-color 0.2s, box-shadow 0.2s',
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = 'rgba(0,128,128,0.6)';
                      e.target.style.boxShadow = '0 0 0 3px rgba(0,128,128,0.15)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(0,128,128,0.3)';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading || !authInstance}
                  style={{
                    width: '100%',
                    padding: 14,
                    background: 'linear-gradient(to right, #008080, #2E8B57)',
                    border: 'none',
                    borderRadius: 12,
                    color: 'white',
                    fontWeight: 600,
                    fontSize: 15,
                    cursor: isLoading || !authInstance ? 'not-allowed' : 'pointer',
                    opacity: isLoading || !authInstance ? 0.7 : 1,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    boxShadow: '0 4px 14px rgba(0, 128, 128, 0.4)',
                  }}
                  onMouseEnter={(e) => {
                    if (!isLoading && authInstance) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 128, 128, 0.5)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 14px rgba(0, 128, 128, 0.4)';
                  }}
                >
                  {isLoading ? 'Signing in...' : 'Sign In'}
                </button>
              </form>

              {/* Divider */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0' }}>
                <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.15)' }} />
                <span style={{ color: '#64748b', fontSize: 12 }}>or</span>
                <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.15)' }} />
              </div>

              {/* SSO Button */}
              <button
                onClick={handleSSOLogin}
                disabled={isLoading || !authInstance}
                style={{
                  width: '100%',
                  padding: 14,
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: 12,
                  color: 'white',
                  fontWeight: 600,
                  fontSize: 15,
                  cursor: isLoading || !authInstance ? 'not-allowed' : 'pointer',
                  opacity: isLoading || !authInstance ? 0.7 : 1,
                  transition: 'background 0.2s, border-color 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                }}
                onMouseEnter={(e) => {
                  if (!isLoading && authInstance) {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.12)';
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)';
                }}
              >
                <svg width={20} height={20} viewBox="0 0 23 23" fill="none">
                  <rect width="11" height="11" fill="#f25022"/>
                  <rect x="12" width="11" height="11" fill="#7fba00"/>
                  <rect y="12" width="11" height="11" fill="#00a4ef"/>
                  <rect x="12" y="12" width="11" height="11" fill="#ffb900"/>
                </svg>
                Culmen Login
              </button>
            </div>

            {/* Footer */}
            <div style={{ padding: '0 32px 24px', textAlign: 'center' }}>
              <p style={{ margin: 0, color: '#99f6e4', fontSize: 12 }}>© Culmen International 2026</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};